// Docs: FEATURES.md "App structure" (wake screen game). Built-in noun deck
// for the der/die/das mini-game on the wake screen (components/WakeGame.jsx).
// Bundled rather than fetched: the game runs precisely while the api is down.
// [article, noun, plural] — plural null for nouns without a common plural.
export const WAKE_NOUNS = [
  ['der', 'Tisch', 'Tische'], ['die', 'Lampe', 'Lampen'], ['das', 'Fenster', 'Fenster'],
  ['der', 'Löffel', 'Löffel'], ['die', 'Gabel', 'Gabeln'], ['das', 'Messer', 'Messer'],
  ['der', 'Käse', null], ['die', 'Butter', null], ['das', 'Brot', 'Brote'],
  ['der', 'Apfel', 'Äpfel'], ['die', 'Birne', 'Birnen'], ['das', 'Ei', 'Eier'],
  ['der', 'Stuhl', 'Stühle'], ['die', 'Tür', 'Türen'], ['das', 'Bett', 'Betten'],
  ['der', 'Schlüssel', 'Schlüssel'], ['die', 'Tasche', 'Taschen'], ['das', 'Handy', 'Handys'],
  ['der', 'Zug', 'Züge'], ['die', 'Straße', 'Straßen'], ['das', 'Auto', 'Autos'],
  ['der', 'Berg', 'Berge'], ['die', 'Stadt', 'Städte'], ['das', 'Dorf', 'Dörfer'],
  ['der', 'Regen', null], ['die', 'Sonne', null], ['das', 'Wetter', null],
  ['der', 'Monat', 'Monate'], ['die', 'Woche', 'Wochen'], ['das', 'Jahr', 'Jahre'],
  ['der', 'Hund', 'Hunde'], ['die', 'Katze', 'Katzen'], ['das', 'Pferd', 'Pferde'],
  ['der', 'Frühling', 'Frühlinge'], ['der', 'Lehrling', 'Lehrlinge'], ['der', 'Optimismus', null],
  ['der', 'Computer', 'Computer'], ['der', 'Honig', null], ['der', 'Kaffee', 'Kaffees'],
  ['die', 'Zeitung', 'Zeitungen'], ['die', 'Freiheit', 'Freiheiten'], ['die', 'Möglichkeit', 'Möglichkeiten'],
  ['die', 'Freundschaft', 'Freundschaften'], ['die', 'Information', 'Informationen'], ['die', 'Universität', 'Universitäten'],
  ['die', 'Bäckerei', 'Bäckereien'], ['die', 'Milch', null], ['die', 'Hand', 'Hände'],
  ['das', 'Mädchen', 'Mädchen'], ['das', 'Büchlein', 'Büchlein'], ['das', 'Dokument', 'Dokumente'],
  ['das', 'Museum', 'Museen'], ['das', 'Wasser', null], ['das', 'Zimmer', 'Zimmer'],
  ['das', 'Gemüse', null], ['das', 'Geschenk', 'Geschenke'], ['das', 'Kind', 'Kinder'],
  ['der', 'Käfig', 'Käfige'], ['die', 'Brücke', 'Brücken'], ['das', 'Schiff', 'Schiffe'],
  ['der', 'Teller', 'Teller'], ['die', 'Tasse', 'Tassen'], ['das', 'Glas', 'Gläser'],
  ['der', 'Sessel', 'Sessel'], ['die', 'Decke', 'Decken'], ['das', 'Kissen', 'Kissen'],
  ['der', 'Mond', 'Monde'], ['die', 'Erde', null], ['das', 'Meer', 'Meere'],
];

// Ending rules worth teaching after an answer — only reliable ones.
const ENDING_RULES = [
  [/(ung|heit|keit|schaft|ion|tät|ei)$/, 'die'],
  [/(chen|lein|ment|um)$/, 'das'],
  [/(ling|ismus)$/, 'der'],
];

export function endingRule(noun, article) {
  for (const [re, ruleArticle] of ENDING_RULES) {
    const m = noun.match(re);
    if (m && ruleArticle === article) return `-${m[1]} → immer ${article}`;
  }
  return null;
}

export function shuffled(list, random = Math.random) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// Perfekt game. [infinitive, auxiliary, participle, a tempting wrong
// participle]. Only verbs whose auxiliary is unambiguous in standard German
// (no fahren/fliegen — "hat das Auto gefahren" — and no liegen/sitzen/stehen,
// which take "sein" in the south).
export const WAKE_VERBS = [
  ['gehen', 'ist', 'gegangen', 'gegeht'],
  ['kommen', 'ist', 'gekommen', 'gekommt'],
  ['laufen', 'ist', 'gelaufen', 'geläuft'],
  ['bleiben', 'ist', 'geblieben', 'gebleibt'],
  ['sein', 'ist', 'gewesen', 'geseint'],
  ['werden', 'ist', 'geworden', 'gewerdet'],
  ['sterben', 'ist', 'gestorben', 'gestirbt'],
  ['wachsen', 'ist', 'gewachsen', 'gewächst'],
  ['aufstehen', 'ist', 'aufgestanden', 'aufgesteht'],
  ['einschlafen', 'ist', 'eingeschlafen', 'eingeschläft'],
  ['passieren', 'ist', 'passiert', 'gepassiert'],
  ['reisen', 'ist', 'gereist', 'gereisen'],
  ['umziehen', 'ist', 'umgezogen', 'umgeziehen'],
  ['essen', 'hat', 'gegessen', 'geesst'],
  ['trinken', 'hat', 'getrunken', 'getrinkt'],
  ['schreiben', 'hat', 'geschrieben', 'geschreibt'],
  ['lesen', 'hat', 'gelesen', 'geliest'],
  ['sehen', 'hat', 'gesehen', 'gesieht'],
  ['nehmen', 'hat', 'genommen', 'genehmt'],
  ['finden', 'hat', 'gefunden', 'gefindet'],
  ['helfen', 'hat', 'geholfen', 'gehelft'],
  ['sprechen', 'hat', 'gesprochen', 'gesprecht'],
  ['denken', 'hat', 'gedacht', 'gedenkt'],
  ['bringen', 'hat', 'gebracht', 'gebringt'],
  ['wissen', 'hat', 'gewusst', 'gewisst'],
  ['kaufen', 'hat', 'gekauft', 'gekaufen'],
  ['arbeiten', 'hat', 'gearbeitet', 'gearbeiten'],
  ['schlafen', 'hat', 'geschlafen', 'geschläft'],
  ['studieren', 'hat', 'studiert', 'gestudiert'],
  ['telefonieren', 'hat', 'telefoniert', 'getelefoniert'],
  ['verstehen', 'hat', 'verstanden', 'geverstanden'],
  ['vergessen', 'hat', 'vergessen', 'gevergessen'],
  ['besuchen', 'hat', 'besucht', 'gebesucht'],
  ['bekommen', 'hat', 'bekommen', 'gebekommen'],
  ['anrufen', 'hat', 'angerufen', 'angeruft'],
  ['mitbringen', 'hat', 'mitgebracht', 'mitgebringt'],
  ['einkaufen', 'hat', 'eingekauft', 'geeinkauft'],
];

// Präposition + Fall game. [phrase with ___, right article, two wrong ones,
// the rule]. Two-way prepositions come in Wo?/Wohin? pairs.
export const WAKE_PREPOSITIONS = [
  ['mit ___ Freund', 'dem', ['den', 'des'], 'mit + Dativ'],
  ['mit ___ Kindern', 'den', ['die', 'der'], 'mit + Dativ (Plural: den …n)'],
  ['für ___ Freund', 'den', ['dem', 'des'], 'für + Akkusativ'],
  ['ohne ___ Mantel', 'den', ['dem', 'der'], 'ohne + Akkusativ'],
  ['durch ___ Park', 'den', ['dem', 'des'], 'durch + Akkusativ'],
  ['gegen ___ Wand', 'die', ['der', 'den'], 'gegen + Akkusativ'],
  ['um ___ Tisch', 'den', ['dem', 'der'], 'um + Akkusativ'],
  ['seit ___ Sommer', 'dem', ['den', 'des'], 'seit + Dativ'],
  ['aus ___ Haus', 'dem', ['das', 'des'], 'aus + Dativ'],
  ['bei ___ Arbeit', 'der', ['die', 'den'], 'bei + Dativ'],
  ['nach ___ Film', 'dem', ['den', 'des'], 'nach + Dativ'],
  ['von ___ Bahnhof', 'dem', ['den', 'der'], 'von + Dativ'],
  ['zu ___ Ärztin', 'der', ['die', 'den'], 'zu + Dativ'],
  ['außer ___ Kind', 'dem', ['das', 'des'], 'außer + Dativ'],
  ['gegenüber ___ Bahnhof', 'dem', ['den', 'des'], 'gegenüber + Dativ'],
  ['wegen ___ Regens', 'des', ['dem', 'den'], 'wegen + Genitiv'],
  ['trotz ___ Wetters', 'des', ['dem', 'das'], 'trotz + Genitiv'],
  ['während ___ Pause', 'der', ['die', 'den'], 'während + Genitiv'],
  ['Ich gehe in ___ Küche.', 'die', ['der', 'den'], 'Wohin? → Akkusativ'],
  ['Ich koche in ___ Küche.', 'der', ['die', 'den'], 'Wo? → Dativ'],
  ['Ich lege das Buch auf ___ Tisch.', 'den', ['dem', 'der'], 'Wohin? → Akkusativ'],
  ['Das Buch liegt auf ___ Tisch.', 'dem', ['den', 'der'], 'Wo? → Dativ'],
  ['Ich hänge das Bild an ___ Wand.', 'die', ['der', 'den'], 'Wohin? → Akkusativ'],
  ['Das Bild hängt an ___ Wand.', 'der', ['die', 'den'], 'Wo? → Dativ'],
  ['Die Katze springt unter ___ Bett.', 'das', ['dem', 'den'], 'Wohin? → Akkusativ'],
  ['Die Katze schläft unter ___ Bett.', 'dem', ['das', 'den'], 'Wo? → Dativ'],
  ['Er stellt das Auto hinter ___ Haus.', 'das', ['dem', 'des'], 'Wohin? → Akkusativ'],
  ['Wir warten vor ___ Kino.', 'dem', ['das', 'den'], 'Wo? → Dativ'],
  ['Die Lampe hängt über ___ Tisch.', 'dem', ['den', 'der'], 'Wo? → Dativ'],
  ['Ich setze mich neben ___ Frau.', 'die', ['der', 'den'], 'Wohin? → Akkusativ'],
  ['Ich sitze neben ___ Frau.', 'der', ['die', 'den'], 'Wo? → Dativ'],
  ['Der Hund läuft zwischen ___ Bäume.', 'die', ['den', 'der'], 'Wohin? → Akkusativ'],
];

// The next decks mirror the app's grammar drills (reflexive, case,
// preposition, conjugation, noun-verb collocation) in one-tap form.
// Gap decks: [phrase with ___, right answer, two wrong ones, the rule].
// Wrong options are checked to be wrong in standard German — no
// "Bescheid sagen"/"Platz machen"-style alternatives that are also fine.

export const WAKE_REFLEXIVES = [
  ['Ich wasche ___.', 'mich', ['mir', 'sich'], 'kein anderes Objekt → Akkusativ'],
  ['Ich wasche ___ die Hände.', 'mir', ['mich', 'sich'], 'schon ein Akkusativobjekt → Dativ'],
  ['Du freust ___ auf den Urlaub.', 'dich', ['dir', 'sich'], 'sich freuen → Akkusativ'],
  ['Ich kann ___ das nicht vorstellen.', 'mir', ['mich', 'sich'], 'sich etwas vorstellen → Dativ'],
  ['Er interessiert ___ für Musik.', 'sich', ['ihn', 'ihm'], 'er / sie / es → immer sich'],
  ['Ich ziehe ___ schnell an.', 'mich', ['mir', 'sich'], 'kein anderes Objekt → Akkusativ'],
  ['Ich ziehe ___ die Jacke an.', 'mir', ['mich', 'sich'], 'schon ein Akkusativobjekt → Dativ'],
  ['Merk ___ das!', 'dir', ['dich', 'sich'], 'sich etwas merken → Dativ'],
  ['Wir treffen ___ um acht.', 'uns', ['euch', 'sich'], 'wir → uns'],
  ['Habt ihr ___ gut erholt?', 'euch', ['uns', 'sich'], 'ihr → euch'],
  ['Ich putze ___ die Zähne.', 'mir', ['mich', 'sich'], 'schon ein Akkusativobjekt → Dativ'],
  ['Du hast ___ erkältet.', 'dich', ['dir', 'sich'], 'sich erkälten → Akkusativ'],
  ['Ich wünsche ___ einen Hund.', 'mir', ['mich', 'sich'], 'sich etwas wünschen → Dativ'],
  ['Die Kinder beeilen ___.', 'sich', ['ihnen', 'sie'], 'sie (Plural) → sich'],
  ['Ich ärgere ___ über den Lärm.', 'mich', ['mir', 'sich'], 'sich ärgern → Akkusativ'],
  ['Hast du ___ wehgetan?', 'dir', ['dich', 'sich'], 'sich wehtun → Dativ'],
  ['Ich verlasse ___ auf dich.', 'mich', ['mir', 'sich'], 'sich verlassen → Akkusativ'],
  ['Kannst du ___ das leisten?', 'dir', ['dich', 'sich'], 'sich etwas leisten → Dativ'],
];

export const WAKE_VERB_CASES = [
  ['Ich helfe ___ Mann.', 'dem', ['den', 'des'], 'helfen + Dativ'],
  ['Ich sehe ___ Mann.', 'den', ['dem', 'des'], 'sehen + Akkusativ'],
  ['Das Buch gehört ___ Lehrerin.', 'der', ['die', 'den'], 'gehören + Dativ'],
  ['Ich frage ___ Lehrer.', 'den', ['dem', 'des'], 'fragen + Akkusativ'],
  ['Ich gratuliere ___ Kollegin.', 'der', ['die', 'den'], 'gratulieren + Dativ'],
  ['Ich rufe ___ Chef an.', 'den', ['dem', 'des'], 'anrufen + Akkusativ'],
  ['Der Film gefällt ___ Kind.', 'dem', ['das', 'den'], 'gefallen + Dativ'],
  ['Ich besuche ___ Freund.', 'den', ['dem', 'des'], 'besuchen + Akkusativ'],
  ['Ich vertraue ___ Ärztin.', 'der', ['die', 'den'], 'vertrauen + Dativ'],
  ['Wir brauchen ___ Schlüssel.', 'den', ['dem', 'des'], 'brauchen + Akkusativ'],
  ['Ich folge ___ Hund.', 'dem', ['den', 'des'], 'folgen + Dativ'],
  ['Er antwortet ___ Lehrer.', 'dem', ['den', 'des'], 'jemandem antworten + Dativ'],
  ['Ich gebe ___ Kind das Buch.', 'dem', ['das', 'den'], 'geben: Person im Dativ, Sache im Akkusativ'],
  ['Sie zeigt ___ Gast das Zimmer.', 'dem', ['den', 'des'], 'zeigen: Person im Dativ, Sache im Akkusativ'],
  ['Ich kenne ___ Stadt gut.', 'die', ['der', 'den'], 'kennen + Akkusativ'],
  ['Die Suppe schmeckt ___ Mann nicht.', 'dem', ['den', 'des'], 'schmecken + Dativ'],
  ['Ich bitte ___ Kellner um die Rechnung.', 'den', ['dem', 'des'], 'bitten + Akkusativ'],
  ['Das Kleid steht ___ Frau gut.', 'der', ['die', 'den'], 'jemandem stehen + Dativ'],
];

export const WAKE_VERB_PREPOSITIONS = [
  ['warten ___ den Bus', 'auf', ['für', 'an'], 'warten auf + Akk'],
  ['sich freuen ___ das Wochenende', 'auf', ['über', 'an'], 'freuen auf = kommt noch'],
  ['sich freuen ___ das Geschenk', 'über', ['auf', 'für'], 'freuen über = ist schon da'],
  ['denken ___ dich', 'an', ['über', 'von'], 'denken an + Akk'],
  ['sich interessieren ___ Kunst', 'für', ['an', 'über'], 'interessieren für + Akk'],
  ['Angst haben ___ Spinnen', 'vor', ['von', 'für'], 'Angst vor + Dat'],
  ['träumen ___ einem Haus', 'von', ['über', 'an'], 'träumen von + Dat'],
  ['teilnehmen ___ dem Kurs', 'an', ['bei', 'in'], 'teilnehmen an + Dat'],
  ['sich kümmern ___ die Kinder', 'um', ['für', 'über'], 'kümmern um + Akk'],
  ['abhängen ___ dem Wetter', 'von', ['an', 'aus'], 'abhängen von + Dat'],
  ['sich erinnern ___ den Urlaub', 'an', ['über', 'von'], 'erinnern an + Akk'],
  ['bitten ___ Hilfe', 'um', ['für', 'nach'], 'bitten um + Akk'],
  ['fragen ___ dem Weg', 'nach', ['für', 'um'], 'fragen nach + Dat'],
  ['sich gewöhnen ___ die Kälte', 'an', ['zu', 'für'], 'gewöhnen an + Akk'],
  ['sich bewerben ___ eine Stelle', 'um', ['für', 'auf'], 'bewerben um + Akk'],
  ['sich beschweren ___ den Lärm', 'über', ['von', 'gegen'], 'beschweren über + Akk'],
  ['achten ___ die Details', 'auf', ['an', 'für'], 'achten auf + Akk'],
  ['sich verlassen ___ dich', 'auf', ['an', 'zu'], 'verlassen auf + Akk'],
  ['aufhören ___ dem Rauchen', 'mit', ['von', 'zu'], 'aufhören mit + Dat'],
  ['sich entschuldigen ___ die Verspätung', 'für', ['über', 'um'], 'entschuldigen für + Akk'],
  ['stolz sein ___ dich', 'auf', ['über', 'für'], 'stolz auf + Akk'],
  ['sich beschäftigen ___ Geschichte', 'mit', ['an', 'über'], 'beschäftigen mit + Dat'],
];

export const WAKE_COLLOCATIONS = [
  ['eine Entscheidung ___', 'treffen', ['machen', 'tun'], 'eine Entscheidung treffen'],
  ['eine Frage ___', 'stellen', ['machen', 'fragen'], 'eine Frage stellen'],
  ['Rücksicht ___', 'nehmen', ['geben', 'machen'], 'Rücksicht nehmen (auf + Akk)'],
  ['einen Fehler ___', 'machen', ['tun', 'stellen'], 'einen Fehler machen'],
  ['eine Rolle ___', 'spielen', ['machen', 'tun'], 'eine Rolle spielen'],
  ['Abschied ___', 'nehmen', ['machen', 'geben'], 'Abschied nehmen (von + Dat)'],
  ['Kritik ___', 'üben', ['machen', 'tun'], 'Kritik üben (an + Dat)'],
  ['Bezug ___', 'nehmen', ['machen', 'geben'], 'Bezug nehmen (auf + Akk)'],
  ['eine Rede ___', 'halten', ['machen', 'geben'], 'eine Rede halten'],
  ['einen Antrag ___', 'stellen', ['machen', 'geben'], 'einen Antrag stellen'],
  ['Wert auf etwas ___', 'legen', ['stellen', 'machen'], 'Wert legen auf + Akk'],
  ['Verantwortung ___', 'übernehmen', ['machen', 'geben'], 'Verantwortung übernehmen'],
  ['etwas zur Verfügung ___', 'stellen', ['geben', 'machen'], 'etwas zur Verfügung stellen'],
  ['eine Ausnahme ___', 'machen', ['geben', 'tun'], 'eine Ausnahme machen'],
  ['ein Gespräch ___', 'führen', ['machen', 'halten'], 'ein Gespräch führen'],
  ['in Kraft ___', 'treten', ['gehen', 'kommen'], 'in Kraft treten'],
  ['Platz ___', 'nehmen', ['setzen', 'stellen'], 'Platz nehmen = sich setzen'],
];

// Präteritum game. [infinitive, er/sie form, two tempting wrong forms].
// Mixed verbs (Vokalwechsel + -te) and the three irregular auxiliaries are
// listed separately for the rule hint.
export const WAKE_PRETERITE = [
  ['gehen', 'ging', ['gehte', 'gang']],
  ['kommen', 'kam', ['kommte', 'kom']],
  ['sehen', 'sah', ['sehte', 'sahte']],
  ['nehmen', 'nahm', ['nehmte', 'nahmte']],
  ['fahren', 'fuhr', ['fahrte', 'fuhrte']],
  ['schreiben', 'schrieb', ['schreibte', 'schrob']],
  ['finden', 'fand', ['findete', 'fund']],
  ['denken', 'dachte', ['denkte', 'dank']],
  ['bringen', 'brachte', ['bringte', 'brang']],
  ['wissen', 'wusste', ['wisste', 'wuss']],
  ['essen', 'aß', ['esste', 'aßte']],
  ['trinken', 'trank', ['trinkte', 'trunk']],
  ['sprechen', 'sprach', ['sprechte', 'sprachte']],
  ['bleiben', 'blieb', ['bleibte', 'blob']],
  ['laufen', 'lief', ['laufte', 'luf']],
  ['schlafen', 'schlief', ['schlafte', 'schluf']],
  ['stehen', 'stand', ['stehte', 'stahn']],
  ['helfen', 'half', ['helfte', 'holf']],
  ['werden', 'wurde', ['werdete', 'wurd']],
  ['sein', 'war', ['seinte', 'ware']],
  ['haben', 'hatte', ['habte', 'hat']],
  ['kennen', 'kannte', ['kennte', 'kannt']],
  ['rufen', 'rief', ['rufte', 'ruf']],
  ['lesen', 'las', ['leste', 'lies']],
  ['tragen', 'trug', ['tragte', 'trag']],
  ['kaufen', 'kaufte', ['kief', 'kauf']],
  ['spielen', 'spielte', ['spiel', 'spol']],
];
export const WAKE_MIXED_VERBS = new Set(['denken', 'bringen', 'wissen', 'kennen']);
export const WAKE_IRREGULAR_VERBS = new Set(['sein', 'haben', 'werden']);
